import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Target } from '@sorare/core/src/atoms/icons/Target';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  BrandM,
  BrandS,
  BrandXL,
} from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { TutorialBezel } from '#src/components/quickPlay/QuickPlayTutorialDialog/TutorialBezel/index.tsx';
import { useScoreAverageCountBySport } from '#src/components/quickPlay/QuickPlayTutorialDialog/utils.tsx';

const Root = styled(Vertical)`
  position: relative;
`;
const BottomWrapper = styled(Horizontal)`
  padding: var(--unit) 0 var(--triple-unit) 0;
`;
const MinimumMessageWrapper = styled.div`
  @media ${tabletAndAbove} {
    position: absolute;
    bottom: calc(-1 * var(--quadruple-unit));
  }
`;

const DEFAULT_MINIMUM_TARGET = 20;

export const Targets = ({ minimumTarget }: { minimumTarget?: number }) => {
  const { sport } = useSportContext();
  const scoreAverageCountBySport = useScoreAverageCountBySport();
  const score = 50;
  const { color } = findThresholdColor(score, sport, 'PLAYER_SCORE');

  return (
    <Root center>
      <TutorialBezel color="var(--c-rewards-orange)">
        <Vertical gap={2.5} center>
          <Vertical center className="text-center">
            <BrandM>
              <FormattedMessage
                id="Targets.title"
                defaultMessage="We set the target"
              />
            </BrandM>
            <BodyM color="var(--c-nd-600)">
              <FormattedMessage
                id="Targets.description2"
                defaultMessage="+x over the L{scoreAverageCount}"
                values={{
                  scoreAverageCount: scoreAverageCountBySport[sport],
                }}
              />
            </BodyM>
          </Vertical>
          <BottomWrapper gap={3}>
            <Vertical gap={0.5} center>
              <BrandXL color={color}>{score}</BrandXL>
              <BrandS>L{scoreAverageCountBySport[sport]}</BrandS>
            </Vertical>
            <Horizontal gap={2}>
              <Vertical gap={0.5} center>
                <BrandXL>+5</BrandXL>
                <BrandS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="QuickPlayTutorialDialog.targets.over"
                    defaultMessage="Over"
                  />
                </BrandS>
              </Vertical>
              <BrandXL color="var(--c-nd-600)">=</BrandXL>
              <Vertical center>
                <BrandXL color="var(--c-rewards-orange)">55</BrandXL>
                <Horizontal gap={0.5}>
                  <BrandS>
                    <FormattedMessage {...fantasy.target} />
                  </BrandS>
                  <Target />
                </Horizontal>
              </Vertical>
            </Horizontal>
          </BottomWrapper>
        </Vertical>
      </TutorialBezel>
      <MinimumMessageWrapper>
        <BodyS>
          <FormattedMessage
            id="QuickplayTutorialDialog.targets.minimum2"
            defaultMessage="Minimum Target: {value}"
            values={{ value: minimumTarget || DEFAULT_MINIMUM_TARGET }}
          />
        </BodyS>
      </MinimumMessageWrapper>
    </Root>
  );
};
